import React, { useState, useEffect } from 'react';
import { Package, Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import apiClient from '../../utils/apiClient';

interface Ingredient {
  id: number;
  name: string;
  category: string;
  unit: string;
  cost_per_unit: number;
  supplier: string;
  min_stock: number;
  max_stock: number;
  current_stock: number;
  shelf_life_days: number;
}

const IngredientManagement: React.FC = () => {
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Ingredient | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newIngredient, setNewIngredient] = useState<Partial<Ingredient>>({
    name: '',
    category: '野菜',
    unit: 'kg',
    cost_per_unit: 0,
    supplier: '',
    min_stock: 0,
    max_stock: 0,
    current_stock: 0,
    shelf_life_days: 7
  });

  const categories = ['肉類', '野菜', '調味料', '粉類', '卵・乳製品', '米・穀物', '油脂類', '飲料', 'その他'];
  const units = ['kg', 'g', '個', 'L', 'ml', '本', 'パック', '枚', '袋'];

  useEffect(() => {
    fetchIngredients();
  }, []);

  const fetchIngredients = async () => {
    try {
      const response = await apiClient.get('/recipe-management/ingredients');
      setIngredients(response.data);
    } catch (error) {
      console.error('材料取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ingredient: Ingredient) => {
    setEditingId(ingredient.id);
    setEditForm({ ...ingredient });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const handleSaveEdit = async () => {
    if (!editForm) return;

    try {
      await apiClient.put(`/recipe-management/ingredients/${editForm.id}`, editForm);
      await fetchIngredients();
      setEditingId(null);
      setEditForm(null);
      alert('材料情報を更新しました');
    } catch (error) {
      console.error('更新エラー:', error);
      alert('更新に失敗しました');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('この材料を削除しますか？')) return;

    try {
      await apiClient.delete(`/recipe-management/ingredients/${id}`);
      await fetchIngredients();
      alert('材料を削除しました');
    } catch (error) {
      console.error('削除エラー:', error);
      alert('削除に失敗しました');
    }
  };

  const handleAddIngredient = async () => {
    try {
      await apiClient.post('/recipe-management/ingredients', newIngredient);
      await fetchIngredients();
      setShowAddForm(false);
      setNewIngredient({
        name: '',
        category: '野菜',
        unit: 'kg',
        cost_per_unit: 0,
        supplier: '',
        min_stock: 0,
        max_stock: 0,
        current_stock: 0,
        shelf_life_days: 7
      });
      alert('材料を追加しました');
    } catch (error) {
      console.error('追加エラー:', error);
      alert('追加に失敗しました');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Package className="w-7 h-7" />
          材料マスター管理
        </h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          新規材料追加
        </button>
      </div>

      {/* 新規追加フォーム */}
      {showAddForm && (
        <div className="mb-6 p-4 border rounded bg-blue-50">
          <h3 className="font-semibold mb-4">新規材料追加</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              placeholder="材料名"
              value={newIngredient.name}
              onChange={(e) => setNewIngredient({ ...newIngredient, name: e.target.value })}
              className="px-3 py-2 border rounded"
            />
            <select
              value={newIngredient.category}
              onChange={(e) => setNewIngredient({ ...newIngredient, category: e.target.value })}
              className="px-3 py-2 border rounded"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <select
              value={newIngredient.unit}
              onChange={(e) => setNewIngredient({ ...newIngredient, unit: e.target.value })}
              className="px-3 py-2 border rounded"
            >
              {units.map(u => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
            <input
              type="number"
              placeholder="単価（1単位あたりの価格）"
              value={newIngredient.cost_per_unit}
              onChange={(e) => setNewIngredient({ ...newIngredient, cost_per_unit: parseFloat(e.target.value) || 0 })}
              className="px-3 py-2 border rounded"
            />
            <input
              type="text"
              placeholder="仕入先（例：○○商店）"
              value={newIngredient.supplier}
              onChange={(e) => setNewIngredient({ ...newIngredient, supplier: e.target.value })}
              className="px-3 py-2 border rounded"
            />
            <input
              type="number"
              placeholder="賞味期限（日数）"
              value={newIngredient.shelf_life_days}
              onChange={(e) => setNewIngredient({ ...newIngredient, shelf_life_days: parseInt(e.target.value) || 0 })}
              className="px-3 py-2 border rounded"
            />
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleAddIngredient}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              追加
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              キャンセル
            </button>
          </div>
        </div>
      )}

      {/* 材料一覧テーブル */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">材料名</th>
              <th className="px-4 py-3 text-left">カテゴリ</th>
              <th className="px-4 py-3 text-left">単位</th>
              <th className="px-4 py-3 text-right">単価</th>
              <th className="px-4 py-3 text-left">仕入先</th>
              <th className="px-4 py-3 text-right">現在庫</th>
              <th className="px-4 py-3 text-right">最小/最大</th>
              <th className="px-4 py-3 text-center">賞味期限</th>
              <th className="px-4 py-3 text-center">操作</th>
            </tr>
          </thead>
          <tbody>
            {ingredients.map((ingredient) => (
              <tr key={ingredient.id} className="border-t hover:bg-gray-50">
                {editingId === ingredient.id && editForm ? (
                  <>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full px-2 py-1 border rounded"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={editForm.category}
                        onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                        className="w-full px-2 py-1 border rounded"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={editForm.unit}
                        onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })}
                        className="w-full px-2 py-1 border rounded"
                      >
                        {units.map(u => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        value={editForm.cost_per_unit}
                        onChange={(e) => setEditForm({ ...editForm, cost_per_unit: parseFloat(e.target.value) || 0 })}
                        className="w-full px-2 py-1 border rounded text-right"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={editForm.supplier}
                        onChange={(e) => setEditForm({ ...editForm, supplier: e.target.value })}
                        className="w-full px-2 py-1 border rounded"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        value={editForm.current_stock}
                        onChange={(e) => setEditForm({ ...editForm, current_stock: parseFloat(e.target.value) || 0 })}
                        className="w-full px-2 py-1 border rounded text-right"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <input
                          type="number"
                          value={editForm.min_stock}
                          onChange={(e) => setEditForm({ ...editForm, min_stock: parseFloat(e.target.value) || 0 })}
                          className="w-16 px-1 py-1 border rounded text-right"
                        />
                        <span>/</span>
                        <input
                          type="number"
                          value={editForm.max_stock}
                          onChange={(e) => setEditForm({ ...editForm, max_stock: parseFloat(e.target.value) || 0 })}
                          className="w-16 px-1 py-1 border rounded text-right"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="number"
                        value={editForm.shelf_life_days}
                        onChange={(e) => setEditForm({ ...editForm, shelf_life_days: parseInt(e.target.value) || 0 })}
                        className="w-16 px-1 py-1 border rounded text-center"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={handleSaveEdit}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3 font-medium">{ingredient.name}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                        {ingredient.category}
                      </span>
                    </td>
                    <td className="px-4 py-3">{ingredient.unit}</td>
                    <td className="px-4 py-3 text-right">¥{ingredient.cost_per_unit || 0}</td>
                    <td className="px-4 py-3">{ingredient.supplier || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-semibold ${
                        ingredient.current_stock < ingredient.min_stock ? 'text-red-600' :
                        ingredient.current_stock > ingredient.max_stock ? 'text-yellow-600' :
                        'text-green-600'
                      }`}>
                        {ingredient.current_stock || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {ingredient.min_stock || 0}/{ingredient.max_stock || 0}
                    </td>
                    <td className="px-4 py-3 text-center">{ingredient.shelf_life_days || 0}日</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => handleEdit(ingredient)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(ingredient.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IngredientManagement;